import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';

const SecurityContext = createContext();

// Initial state
const initialState = {
  securityEvents: [],
  threatLevel: 'low',
  securitySettings: {
    encryption: {
      dataAtRest: true,
      dataInTransit: true,
      algorithm: 'AES-256'
    },
    authentication: {
      twoFactorRequired: false,
      passwordMinLength: 8,
      passwordComplexity: true,
      sessionTimeout: 30, // minutes
      maxLoginAttempts: 5,
      lockoutDuration: 15 // minutes
    },
    authorization: {
      rbacEnabled: true,
      permissionInheritance: true,
      apiKeyRequired: true
    },
    monitoring: {
      realTimeAlerts: true,
      auditLogging: true,
      suspiciousActivityDetection: true,
      geoLocationTracking: true
    },
    compliance: {
      gdprCompliant: true,
      dataRetentionPeriod: 24, // months
      rightToErasure: true,
      dataPortability: true,
      consentManagement: true
    }
  },
  vulnerabilities: [],
  complianceReport: {
    gdpr: {
      score: 95,
      lastAudit: null,
      issues: []
    },
    iso27001: {
      score: 88,
      lastAudit: null,
      issues: []
    },
    pci: {
      score: 92,
      lastAudit: null,
      issues: []
    }
  },
  backupStatus: {
    lastBackup: null,
    backupSchedule: 'daily',
    retentionPeriod: 30,
    storageLocation: 'encrypted-cloud',
    status: 'healthy'
  },
  certificates: {
    ssl: {
      provider: 'Let\'s Encrypt',
      expiryDate: '2025-04-15',
      status: 'valid',
      autoRenewal: true
    },
    domain: {
      validationLevel: 'Extended Validation',
      issuer: 'DigiCert',
      expiryDate: '2025-12-31',
      status: 'valid'
    }
  },
  isLoading: false,
  error: null
};

// Action types
const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  ADD_SECURITY_EVENT: 'ADD_SECURITY_EVENT',
  SET_SECURITY_EVENTS: 'SET_SECURITY_EVENTS',
  UPDATE_THREAT_LEVEL: 'UPDATE_THREAT_LEVEL',
  UPDATE_SECURITY_SETTINGS: 'UPDATE_SECURITY_SETTINGS',
  SET_VULNERABILITIES: 'SET_VULNERABILITIES',
  UPDATE_COMPLIANCE_REPORT: 'UPDATE_COMPLIANCE_REPORT',
  UPDATE_BACKUP_STATUS: 'UPDATE_BACKUP_STATUS',
  UPDATE_CERTIFICATES: 'UPDATE_CERTIFICATES'
};

// Reducer
function securityReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload };
    
    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, isLoading: false };
    
    case ACTIONS.ADD_SECURITY_EVENT:
      return {
        ...state,
        securityEvents: [action.payload, ...state.securityEvents].slice(0, 100) // Keep last 100
      };
    
    case ACTIONS.SET_SECURITY_EVENTS:
      return { ...state, securityEvents: action.payload, isLoading: false };
    
    case ACTIONS.UPDATE_THREAT_LEVEL:
      return { ...state, threatLevel: action.payload };
    
    case ACTIONS.UPDATE_SECURITY_SETTINGS:
      return {
        ...state,
        securitySettings: { ...state.securitySettings, ...action.payload },
        isLoading: false
      };
    
    case ACTIONS.SET_VULNERABILITIES:
      return { ...state, vulnerabilities: action.payload, isLoading: false };
    
    case ACTIONS.UPDATE_COMPLIANCE_REPORT:
      return {
        ...state,
        complianceReport: { ...state.complianceReport, ...action.payload },
        isLoading: false
      };
    
    case ACTIONS.UPDATE_BACKUP_STATUS:
      return {
        ...state,
        backupStatus: { ...state.backupStatus, ...action.payload },
        isLoading: false
      };
    
    case ACTIONS.UPDATE_CERTIFICATES:
      return {
        ...state,
        certificates: { ...state.certificates, ...action.payload },
        isLoading: false
      };
    
    default:
      return state;
  }
}

// Context Provider
export const SecurityProvider = ({ children }) => {
  const [state, dispatch] = useReducer(securityReducer, initialState);
  const { user } = useAuth();

  // Load initial security data
  useEffect(() => {
    if (user) {
      loadSecurityData();
      startSecurityMonitoring();
    }
  }, [user]);

  const loadSecurityData = useCallback(async () => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    
    try {
      await Promise.all([
        loadSecurityEvents(),
        loadVulnerabilities(),
        loadComplianceReport(),
        updateBackupStatus(),
        checkCertificates()
      ]);
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
    }
  }, []);

  const loadSecurityEvents = useCallback(async () => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const mockEvents = [
        {
          id: 'sec_001',
          type: 'login_success',
          severity: 'info',
          userId: 'user_001',
          userName: 'Juan Pérez',
          description: 'Inicio de sesión exitoso desde ubicación conocida',
          ipAddress: '192.168.1.100',
          userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
          location: 'Ciudad de México, México',
          timestamp: new Date().toISOString(),
          risk: 'low'
        },
        {
          id: 'sec_002',
          type: 'failed_login',
          severity: 'warning',
          userId: null,
          userName: 'unknown',
          description: 'Múltiples intentos de inicio de sesión fallidos',
          ipAddress: '203.0.113.45',
          userAgent: 'curl/7.68.0',
          location: 'Desconocida',
          timestamp: new Date(Date.now() - 300000).toISOString(),
          risk: 'medium'
        },
        {
          id: 'sec_003',
          type: 'suspicious_activity',
          severity: 'high',
          userId: 'user_003',
          userName: 'Carlos Rodríguez',
          description: 'Acceso desde ubicación inusual',
          ipAddress: '198.51.100.23',
          userAgent: 'Mozilla/5.0 (Android 10; Mobile; rv:81.0) Gecko/81.0 Firefox/81.0',
          location: 'Moscú, Rusia',
          timestamp: new Date(Date.now() - 600000).toISOString(),
          risk: 'high'
        },
        {
          id: 'sec_004',
          type: 'api_rate_limit',
          severity: 'warning',
          userId: null,
          userName: 'system',
          description: 'Rate limit excedido en API de cotizaciones',
          ipAddress: '104.16.123.45',
          userAgent: 'PostmanRuntime/7.28.4',
          location: 'Estados Unidos',
          timestamp: new Date(Date.now() - 900000).toISOString(),
          risk: 'medium'
        },
        {
          id: 'sec_005',
          type: 'data_access',
          severity: 'info',
          userId: 'admin_001',
          userName: 'Admin Silva',
          description: 'Acceso a datos de usuario para soporte técnico',
          ipAddress: '192.168.1.1',
          userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
          location: 'Ciudad de México, México',
          timestamp: new Date(Date.now() - 1200000).toISOString(),
          risk: 'low'
        }
      ];
      
      dispatch({ type: ACTIONS.SET_SECURITY_EVENTS, payload: mockEvents });
      
      // Update threat level based on events
      const highRiskEvents = mockEvents.filter(e => e.risk === 'high').length;
      const mediumRiskEvents = mockEvents.filter(e => e.risk === 'medium').length;
      
      let threatLevel = 'low';
      if (highRiskEvents > 0) {
        threatLevel = 'high';
      } else if (mediumRiskEvents > 2) {
        threatLevel = 'medium';
      }
      
      dispatch({ type: ACTIONS.UPDATE_THREAT_LEVEL, payload: threatLevel });
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
    }
  }, []);

  const loadVulnerabilities = useCallback(async () => {
    try {
      // Simulate vulnerability scan
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const mockVulnerabilities = [
        {
          id: 'vuln_001',
          type: 'outdated_dependency',
          severity: 'medium',
          title: 'Dependencia desactualizada: react-scripts',
          description: 'La versión actual de react-scripts tiene vulnerabilidades conocidas',
          affected: 'Frontend',
          cvss: 5.3,
          cve: 'CVE-2023-1234',
          discoveredAt: '2025-01-15T10:00:00Z',
          status: 'open',
          solution: 'Actualizar react-scripts a la versión 5.0.2 o superior'
        },
        {
          id: 'vuln_002',
          type: 'weak_cipher',
          severity: 'low',
          title: 'Cifrado débil en configuración legacy',
          description: 'Algunas configuraciones legacy utilizan cifrado MD5',
          affected: 'Backend Legacy',
          cvss: 3.1,
          cve: null,
          discoveredAt: '2025-01-14T15:30:00Z',
          status: 'acknowledged',
          solution: 'Migrar configuraciones legacy a SHA-256'
        },
        {
          id: 'vuln_003',
          type: 'configuration',
          severity: 'high',
          title: 'CORS configurado demasiado permisivo',
          description: 'La configuración CORS permite cualquier origen en desarrollo',
          affected: 'API Gateway',
          cvss: 7.2,
          cve: null,
          discoveredAt: '2025-01-16T09:00:00Z',
          status: 'fixed',
          solution: 'Configurar CORS con orígenes específicos para producción'
        }
      ];
      
      dispatch({ type: ACTIONS.SET_VULNERABILITIES, payload: mockVulnerabilities });
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
    }
  }, []);

  const loadComplianceReport = useCallback(async () => {
    try {
      // Simulate compliance check
      await new Promise(resolve => setTimeout(resolve, 1200));
      
      const mockCompliance = {
        gdpr: {
          score: 95,
          lastAudit: '2025-01-01T00:00:00Z',
          issues: [
            'Política de cookies requiere actualización',
            'Formulario de consentimiento pendiente en módulo de registro'
          ]
        },
        iso27001: {
          score: 88,
          lastAudit: '2024-12-15T00:00:00Z',
          issues: [
            'Política de gestión de incidentes requiere revisión',
            'Inventario de activos incompleto',
            'Plan de continuidad de negocio pendiente'
          ]
        },
        pci: {
          score: 92,
          lastAudit: '2025-01-10T00:00:00Z',
          issues: [
            'Logs de acceso a datos de tarjetas requieren mayor retención',
            'Validación de entrada en formularios de pago'
          ]
        }
      };
      
      dispatch({ type: ACTIONS.UPDATE_COMPLIANCE_REPORT, payload: mockCompliance });
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
    }
  }, []);

  const updateBackupStatus = useCallback(async () => {
    try {
      // Simulate backup status check
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const backupStatus = {
        lastBackup: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(), // 4 hours ago
        backupSchedule: 'daily',
        retentionPeriod: 30,
        storageLocation: 'encrypted-cloud',
        status: 'healthy',
        size: '2.4 GB',
        encrypted: true
      };
      
      dispatch({ type: ACTIONS.UPDATE_BACKUP_STATUS, payload: backupStatus });
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
    }
  }, []);

  const checkCertificates = useCallback(async () => {
    try {
      // Simulate certificate check
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const certificates = {
        ssl: {
          provider: 'Let\'s Encrypt',
          expiryDate: '2025-04-15T00:00:00Z',
          status: 'valid',
          autoRenewal: true,
          daysUntilExpiry: 89
        },
        domain: {
          validationLevel: 'Extended Validation',
          issuer: 'DigiCert',
          expiryDate: '2025-12-31T00:00:00Z',
          status: 'valid',
          daysUntilExpiry: 349
        }
      };
      
      dispatch({ type: ACTIONS.UPDATE_CERTIFICATES, payload: certificates });
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
    }
  }, []);

  // Real-time security monitoring
  const startSecurityMonitoring = useCallback(() => {
    // Simulate periodic security events
    const interval = setInterval(() => {
      const eventTypes = ['login_success', 'api_access', 'data_access', 'backup_completed'];
      const randomEvent = eventTypes[Math.floor(Math.random() * eventTypes.length)];
      
      const securityEvent = {
        id: `sec_${Date.now()}`,
        type: randomEvent,
        severity: 'info',
        userId: user?.id,
        userName: user?.name,
        description: `Evento de seguridad: ${randomEvent}`,
        ipAddress: '192.168.1.' + Math.floor(Math.random() * 255),
        userAgent: 'Mozilla/5.0 (Auto-Generated)',
        location: 'Ciudad de México, México',
        timestamp: new Date().toISOString(),
        risk: 'low'
      };
      
      dispatch({ type: ACTIONS.ADD_SECURITY_EVENT, payload: securityEvent });
    }, 30000); // Every 30 seconds
    
    return () => clearInterval(interval);
  }, [user]);

  // Security actions
  const runVulnerabilityScan = useCallback(async () => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    
    try {
      // Simulate vulnerability scan
      await new Promise(resolve => setTimeout(resolve, 3000));
      await loadVulnerabilities();
      
      return { success: true, message: 'Escaneo de vulnerabilidades completado' };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    }
  }, [loadVulnerabilities]);

  const runComplianceAudit = useCallback(async () => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    
    try {
      // Simulate compliance audit
      await new Promise(resolve => setTimeout(resolve, 5000));
      await loadComplianceReport();
      
      return { success: true, message: 'Auditoría de cumplimiento completada' };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    }
  }, [loadComplianceReport]);

  const performBackup = useCallback(async () => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    
    try {
      // Simulate backup process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const newBackupStatus = {
        lastBackup: new Date().toISOString(),
        status: 'healthy',
        size: (Math.random() * 2 + 1.5).toFixed(1) + ' GB'
      };
      
      dispatch({ type: ACTIONS.UPDATE_BACKUP_STATUS, payload: newBackupStatus });
      
      return { success: true, message: 'Backup completado exitosamente' };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    }
  }, []);

  const updateSecuritySettings = useCallback(async (settings) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      dispatch({ type: ACTIONS.UPDATE_SECURITY_SETTINGS, payload: settings });
      
      // Log security settings change
      const securityEvent = {
        id: `sec_${Date.now()}`,
        type: 'settings_changed',
        severity: 'warning',
        userId: user?.id,
        userName: user?.name,
        description: 'Configuración de seguridad actualizada',
        ipAddress: '192.168.1.100',
        userAgent: 'Browser',
        location: 'Ciudad de México, México',
        timestamp: new Date().toISOString(),
        risk: 'low'
      };
      
      dispatch({ type: ACTIONS.ADD_SECURITY_EVENT, payload: securityEvent });
      
      return { success: true };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    }
  }, [user]);

  // Analytics helpers
  const getSecurityMetrics = useCallback(() => {
    const today = new Date();
    const last24h = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const last7d = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    const events24h = state.securityEvents.filter(e => new Date(e.timestamp) > last24h);
    const events7d = state.securityEvents.filter(e => new Date(e.timestamp) > last7d);
    
    return {
      totalEvents24h: events24h.length,
      totalEvents7d: events7d.length,
      highRiskEvents24h: events24h.filter(e => e.risk === 'high').length,
      failedLogins24h: events24h.filter(e => e.type === 'failed_login').length,
      suspiciousActivity24h: events24h.filter(e => e.type === 'suspicious_activity').length,
      averageRiskScore: state.securityEvents.length > 0 
        ? state.securityEvents.reduce((sum, e) => {
            const riskScore = e.risk === 'high' ? 3 : e.risk === 'medium' ? 2 : 1;
            return sum + riskScore;
          }, 0) / state.securityEvents.length
        : 1
    };
  }, [state.securityEvents]);

  const getSecurityTrends = useCallback(() => {
    // Generate trend data for the last 7 days
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(Date.now() - (6 - i) * 24 * 60 * 60 * 1000);
      const dayEvents = state.securityEvents.filter(e => {
        const eventDate = new Date(e.timestamp);
        return eventDate.toDateString() === date.toDateString();
      });
      
      return {
        date: date.toISOString().split('T')[0],
        events: dayEvents.length,
        high: dayEvents.filter(e => e.risk === 'high').length,
        medium: dayEvents.filter(e => e.risk === 'medium').length,
        low: dayEvents.filter(e => e.risk === 'low').length
      };
    });
  }, [state.securityEvents]);

  const value = {
    // State
    ...state,
    
    // Actions
    runVulnerabilityScan,
    runComplianceAudit,
    performBackup,
    updateSecuritySettings,
    
    // Analytics
    getSecurityMetrics,
    getSecurityTrends,
    
    // Utility functions
    clearError: () => dispatch({ type: ACTIONS.SET_ERROR, payload: null }),
    refreshSecurityData: loadSecurityData
  };

  return (
    <SecurityContext.Provider value={value}>
      {children}
    </SecurityContext.Provider>
  );
};

// Custom hook to use the context
export const useSecurity = () => {
  const context = useContext(SecurityContext);
  if (!context) {
    throw new Error('useSecurity must be used within a SecurityProvider');
  }
  return context;
};

export default SecurityContext;